import { useState } from 'react'
import PaginationControls, { paginateItems } from '../components/PaginationControls'
import { useApp } from '../context/AppContext'
import { isPerfilDiretivo } from '../lib/accessControl'
import { lumi } from '../lib/lumi'
import { persistDeletedVersion } from '../lib/lumiAppendOnly'
import { gerarCodigo, nowISO, formatCurrency } from '../utils'
import type { Aprovacao, ItemOrcado } from '../types'

const UNIDADES_MEDIDA_PADRAO = [
  'un',
  'pc',
  'cj',
  'cx',
  'sc',
  'kg',
  't',
  'm',
  'm2',
  'm3',
  'L',
  'h',
  'dia',
  'mes',
  'vb',
]

type IdentifiableRecord = {
  _id?: string
  id?: string
}

const APP_CREATOR_ID = 'app-4s'
const APP_CREATOR_EMAIL = 'sistema@app4s.local'

function refs(record: IdentifiableRecord | undefined) {
  return [record?.id, record?._id].filter(Boolean).map(String)
}

function sameRef(left: string | undefined, right: string | undefined, records: IdentifiableRecord[] = []) {
  if (!left || !right) return false
  if (left === right) return true

  return records.some(record => {
    const recordRefs = refs(record)
    return recordRefs.includes(left) && recordRefs.includes(right)
  })
}

function findByRef<T extends IdentifiableRecord>(records: T[], ref: string | undefined) {
  if (!ref) return undefined
  return records.find(record => refs(record).includes(ref))
}

function removeUndefinedFields<T extends Record<string, unknown>>(payload: T): T {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  ) as T
}

async function persistAppendOnly(entityName: string, record: Record<string, unknown>) {
  const entity = (lumi.entities as Record<string, { create: (data: Record<string, unknown>) => Promise<unknown> }>)[entityName]
  await entity.create(removeUndefinedFields({
    ...record,
    creator: record.creator ?? APP_CREATOR_ID,
    creatorEmail: record.creatorEmail ?? APP_CREATOR_EMAIL,
    updatedAt: nowISO(),
  }))
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="modal-overlay" style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="modal-panel" style={{ width: 520, maxHeight: '90vh', overflowY: 'auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 className="modal-panel-title" style={{ fontSize: 15, fontWeight: 600 }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#62666d', cursor: 'pointer', fontSize: 18 }}>Ã—</button>
        </div>
        {children}
      </div>
    </div>
  )
}

export default function Orcamento() {
  const { obras, centrosCusto, categorias: categoriasOrcamentarias, itensOrcados, setItensOrcados, aprovacoes, setAprovacoes, currentUser, addLog } = useApp()
  const [filtroObra, setFiltroObra] = useState('')
  const [filtroCateg, setFiltroCateg] = useState('')
  const [busca, setBusca] = useState('')
  const [modal, setModal] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [form, setForm] = useState<Record<string, string>>({})
  const [erro, setErro] = useState('')
  const [toast, setToast] = useState('')
  const [unidadesMedida, setUnidadesMedida] = useState(UNIDADES_MEDIDA_PADRAO)
  const [showNovaUnidade, setShowNovaUnidade] = useState(false)
  const [novaUnidade, setNovaUnidade] = useState('')
  const [justificativaAlteracao, setJustificativaAlteracao] = useState('')
  const [saving, setSaving] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  function showToast(msg: string) { setToast(msg); setTimeout(() => setToast(''), 3000) }
  function field(k: string, v: string) { setForm(p => ({ ...p, [k]: v })) }

  function addUnidadeMedida() {
    const unidade = novaUnidade.trim()
    if (!unidade) return
    setUnidadesMedida(prev => prev.some(item => item.toLowerCase() === unidade.toLowerCase()) ? prev : [...prev, unidade])
    field('unidade_medida', unidade)
    setNovaUnidade('')
    setShowNovaUnidade(false)
  }

  const qtd = parseFloat(form.quantidade_prevista || '0')
  const vun = parseFloat(form.valor_unitario_previsto || '0')
  const total = qtd * vun

  function getUltimaSolicitacaoAlteracao(itemId: string) {
    return aprovacoes
      .filter(a => a.entidade_tipo === 'ALTERACAO_ORCAMENTO' && a.entidade_id === itemId && a.tipo_alteracao_orcamento !== 'EXCLUSAO')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
  }

  function getUltimaSolicitacaoExclusao(itemId: string) {
    return aprovacoes
      .filter(a => a.entidade_tipo === 'ALTERACAO_ORCAMENTO' && a.entidade_id === itemId && a.tipo_alteracao_orcamento === 'EXCLUSAO')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
  }

  function isAlteracaoLiberada(item: ItemOrcado | undefined) {
    if (!item) return false
    const solicitacao = getUltimaSolicitacaoAlteracao(item.id)
    return solicitacao?.status === 'APROVADO' && !solicitacao.liberacao_utilizada
  }

  function isItemBloqueado(item: ItemOrcado | undefined) {
    return Boolean(item?.etapa_referencia) && !isAlteracaoLiberada(item)
  }

  function hasSolicitacaoPendente(itemId: string) {
    return aprovacoes.some(a => a.entidade_tipo === 'ALTERACAO_ORCAMENTO' && a.entidade_id === itemId && a.tipo_alteracao_orcamento !== 'EXCLUSAO' && a.status === 'PENDENTE')
  }

  function hasSolicitacaoExclusaoPendente(itemId: string) {
    return aprovacoes.some(a => a.entidade_tipo === 'ALTERACAO_ORCAMENTO' && a.entidade_id === itemId && a.tipo_alteracao_orcamento === 'EXCLUSAO' && a.status === 'PENDENTE')
  }

  function buildItemAlterado(item: ItemOrcado): Partial<ItemOrcado> {
    return {
      obra_id: form.obra_id,
      centro_custo_id: form.centro_custo_id,
      categoria_orcamentaria_id: form.categoria_orcamentaria_id,
      item_teto_orcamentario: form.item_teto_orcamentario,
      nome_item: form.nome_item,
      descricao: form.descricao,
      unidade_medida: form.unidade_medida || 'un',
      quantidade_prevista: qtd,
      valor_unitario_previsto: vun,
      valor_total_previsto: total,
      saldo_atual: total - item.valor_comprometido - item.valor_pago,
      etapa_referencia: form.etapa_referencia,
      observacoes: form.observacoes,
    }
  }

  async function save() {
    if (!form.obra_id) { setErro('Selecione a obra.'); return }
    if (!form.categoria_orcamentaria_id) { setErro('Selecione a categoria.'); return }
    if (!form.nome_item?.trim()) { setErro('Nome do item é obrigatório.'); return }

    setSaving(true)

    try {
    if (editId) {
      const itemAtual = itensOrcados.find(item => item.id === editId)
      if (!itemAtual) return
      const dadosAlteracao = buildItemAlterado(itemAtual)
      const isAdministrador = isPerfilDiretivo(currentUser?.perfil)

      if (isAdministrador && itemAtual.etapa_referencia && !justificativaAlteracao.trim()) {
        setErro('Informe a justificativa para alterar este item do orcamento.')
        return
      }

      if (!isAdministrador && isItemBloqueado(itemAtual)) {
        if (hasSolicitacaoPendente(editId)) {
          setErro('Ja existe uma solicitacao pendente para alterar este item.')
          return
        }
        if (!justificativaAlteracao.trim()) {
          setErro('Informe a justificativa para solicitar liberacao da alteracao.')
          return
        }

        const aprovacao: Aprovacao = {
          id: 'ap-orc-' + Date.now(),
          entidade_tipo: 'ALTERACAO_ORCAMENTO',
          entidade_id: editId,
          etapa_aprovacao: 'DIRETORIA',
          aprovador_nome: '',
          perfil_aprovador: 'DIRETORIA',
          solicitante_usuario_id: currentUser?.id,
          solicitante_nome: currentUser?.nome,
          solicitante_perfil: currentUser?.perfil,
          status: 'PENDENTE',
          tipo_alteracao_orcamento: 'ALTERACAO',
          observacao: justificativaAlteracao.trim(),
          dados_alteracao_orcamento: dadosAlteracao,
          createdAt: nowISO(),
        }

        await persistAppendOnly('aprovacoes', aprovacao as unknown as Record<string, unknown>)
        setAprovacoes(prev => [aprovacao, ...prev])
        addLog('ALTERACAO_ORCAMENTO', editId, 'CRIADO', `Solicitacao de alteracao enviada para aprovacao. Justificativa: ${justificativaAlteracao.trim()}`)
        showToast('Alteracao aguardando aprovacao')
        setModal(false)
        setJustificativaAlteracao('')
        return
      }

      const itemAtualizado = { ...itemAtual, ...dadosAlteracao } as ItemOrcado
      await persistAppendOnly('itens_orcados', itemAtualizado as unknown as Record<string, unknown>)
      setItensOrcados(p => p.map(i => i.id === editId ? itemAtualizado : i))
      setAprovacoes(prev => prev.map(a =>
        a.id === getUltimaSolicitacaoAlteracao(editId)?.id
          ? { ...a, liberacao_utilizada: true }
          : a,
      ))
      addLog(
        'ITEM_ORCADO',
        editId,
        'EDITADO',
        isAdministrador && itemAtual.etapa_referencia
          ? `Item orcado editado pelo administrador. Justificativa: ${justificativaAlteracao.trim()}`
          : 'Item orcado editado',
      )
      showToast('Item atualizado.')
    } else {
      const n: ItemOrcado = { id: 'io' + Date.now(), obra_id: form.obra_id, centro_custo_id: form.centro_custo_id, categoria_orcamentaria_id: form.categoria_orcamentaria_id, codigo_item: gerarCodigo(), item_teto_orcamentario: form.item_teto_orcamentario, nome_item: form.nome_item, descricao: form.descricao, unidade_medida: form.unidade_medida || 'un', quantidade_prevista: qtd, valor_unitario_previsto: vun, valor_total_previsto: total, valor_comprometido: 0, valor_pago: 0, saldo_atual: total, etapa_referencia: form.etapa_referencia, status: 'ATIVO', observacoes: form.observacoes, createdAt: nowISO() }
      await persistAppendOnly('itens_orcados', n as unknown as Record<string, unknown>)
      setItensOrcados(p => [...p, n])
      addLog('ITEM_ORCADO', n.id, 'CRIADO', `Item orçado ${n.nome_item} cadastrado`)
      showToast('Item orçado cadastrado.')
    }
    setModal(false)
    setJustificativaAlteracao('')
    } catch (error) {
      console.error('Falha ao confirmar item orcado no Lumi:', error)
      showToast('Erro, tente novamente mais tarde.')
    } finally {
      setSaving(false)
    }
  }

  async function deleteItem(item: ItemOrcado) {
    if (isPerfilDiretivo(currentUser?.perfil)) {
      const confirmed = window.confirm(`Excluir o item orçado ${item.nome_item}? Esta ação não pode ser desfeita.`)
      if (!confirmed) return

      try {
        await persistDeletedVersion('itens_orcados', item as unknown as Record<string, unknown>)
      } catch {
        showToast('Erro, tente novamente mais tarde.')
        return
      }
      setItensOrcados(p => p.filter(i => i.id !== item.id))
      addLog('ITEM_ORCADO', item.id, 'CANCELADO', `Item orçado ${item.nome_item} excluído por administrador`)
      showToast('Item orçado excluído.')
      return
    }

    if (hasSolicitacaoExclusaoPendente(item.id)) {
      showToast('Alteracao aguardando aprovacao')
      return
    }

    const confirmed = window.confirm(`Solicitar aprovacao para excluir o item orcado ${item.nome_item}?`)
    if (!confirmed) return

    const aprovacao: Aprovacao = {
      id: 'ap-exc-orc-' + Date.now(),
      entidade_tipo: 'ALTERACAO_ORCAMENTO',
      entidade_id: item.id,
      etapa_aprovacao: 'DIRETORIA',
      aprovador_nome: '',
      perfil_aprovador: 'DIRETORIA',
      solicitante_usuario_id: currentUser?.id,
      solicitante_nome: currentUser?.nome,
      solicitante_perfil: currentUser?.perfil,
      status: 'PENDENTE',
      tipo_alteracao_orcamento: 'EXCLUSAO',
      observacao: `Solicitação de exclusão do item orçado ${item.codigo_item} - ${item.nome_item}.`,
      dados_alteracao_orcamento: { status: 'CANCELADO' },
      createdAt: nowISO(),
    }

    try {
      await persistAppendOnly('aprovacoes', aprovacao as unknown as Record<string, unknown>)
      setAprovacoes(prev => [aprovacao, ...prev])
      addLog('ALTERACAO_ORCAMENTO', item.id, 'CRIADO', `Solicitacao de exclusao do item ${item.nome_item} enviada para aprovacao`)
      showToast('Alteracao aguardando aprovacao')
    } catch (error) {
      console.error('Falha ao confirmar solicitacao de exclusao no Lumi:', error)
      showToast('Erro, tente novamente mais tarde.')
    }
  }

  const itens = itensOrcados.filter(i =>
    (!filtroObra || sameRef(i.obra_id, filtroObra, obras)) &&
    (!filtroCateg || sameRef(i.categoria_orcamentaria_id, filtroCateg, categoriasOrcamentarias)) &&
    (!busca || i.nome_item.toLowerCase().includes(busca.toLowerCase()))
  )

  const totalOrc = itens.reduce((s, i) => s + i.valor_total_previsto, 0)
  const totalComp = itens.reduce((s, i) => s + i.valor_comprometido, 0)
  const paginatedItens = paginateItems(itens, page, pageSize)
  const totalPago = itens.reduce((s, i) => s + i.valor_pago, 0)
  const totalSaldo = itens.reduce((s, i) => s + i.saldo_atual, 0)

  const inp = (label: string, key: string, type = 'text', disabled = false) => (
    <div style={{ marginBottom: 12 }}>
      <label className="label-field">{label}</label>
      <input className="input-field" type={type} value={form[key] || ''} onChange={e => field(key, e.target.value)} disabled={disabled} style={disabled ? { opacity: 0.5 } : {}} />
    </div>
  )
  const sel = (label: string, key: string, options: { value: string; label: string }[]) => (
    <div style={{ marginBottom: 12 }}>
      <label className="label-field">{label}</label>
      <select className="input-field" value={form[key] || ''} onChange={e => field(key, e.target.value)}>
        <option value="">Selecione...</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  )

  const centrosFiltrados = centrosCusto.filter(c => !form.obra_id || sameRef(c.obra_id, form.obra_id, obras))
  const itemEmEdicao = editId ? itensOrcados.find(item => item.id === editId) : undefined
  const isAdministrador = isPerfilDiretivo(currentUser?.perfil)
  const itemBloqueado = !isAdministrador && isItemBloqueado(itemEmEdicao)
  const itemExigeJustificativaAdmin = isAdministrador && Boolean(itemEmEdicao?.etapa_referencia)
  const solicitacaoPendente = editId ? hasSolicitacaoPendente(editId) : false
  const unidadesMedidaOptions = Array.from(new Set([
    ...unidadesMedida,
    ...itensOrcados.map(item => item.unidade_medida).filter(Boolean),
  ]))

  return (
    <div>
      {toast && <div style={{ position: 'fixed', top: 20, right: 20, background: '#1a2e1a', border: '1px solid #34d399', color: '#34d399', padding: '10px 16px', borderRadius: 8, zIndex: 100, fontSize: 13 }}>{toast}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ color: '#f7f8f8', fontSize: 18, fontWeight: 600 }}>Orçamento Base</h1>
          <p style={{ color: '#62666d', fontSize: 13, marginTop: 2 }}>Itens orçados por obra e categoria</p>
        </div>
        <button className="btn-primary" onClick={() => { setForm({}); setEditId(null); setErro(''); setJustificativaAlteracao(''); setShowNovaUnidade(false); setNovaUnidade(''); setModal(true) }}>+ Item Orçado</button>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <select className="input-field" style={{ width: 200 }} value={filtroObra} onChange={e => { setFiltroObra(e.target.value); setPage(1) }}>
          <option value="">Todas as obras</option>
          {obras.map(o => <option key={o.id} value={o.id}>{o.nome_obra}</option>)}
        </select>
        <select className="input-field" style={{ width: 200 }} value={filtroCateg} onChange={e => { setFiltroCateg(e.target.value); setPage(1) }}>
          <option value="">Todas as categorias</option>
          {categoriasOrcamentarias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
        <input className="input-field" style={{ flex: 1 }} placeholder="Buscar item..." value={busca} onChange={e => { setBusca(e.target.value); setPage(1) }} />
      </div>

      {/* Resumo */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
        {[
          { label: 'Orçado', val: totalOrc, color: '#f7f8f8' },
          { label: 'Comprometido', val: totalComp, color: '#fbbf24' },
          { label: 'Pago', val: totalPago, color: '#34d399' },
          { label: 'Saldo', val: totalSaldo, color: totalSaldo < 0 ? '#f87171' : '#60a5fa' },
        ].map(k => (
          <div key={k.label} className="card" style={{ padding: '12px 16px' }}>
            <p style={{ color: '#62666d', fontSize: 11, marginBottom: 4, textTransform: 'uppercase' }}>{k.label}</p>
            <p style={{ color: k.color, fontSize: 16, fontWeight: 600 }}>{formatCurrency(k.val)}</p>
          </div>
        ))}
      </div>

      <div className="card">
        {itens.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#62666d', fontSize: 13 }}>Nenhum item orçado encontrado. <button className="btn-ghost" style={{ marginLeft: 8 }} onClick={() => { setForm({}); setEditId(null); setErro(''); setJustificativaAlteracao(''); setShowNovaUnidade(false); setNovaUnidade(''); setModal(true) }}>+ Novo Item</button></div>
        ) : (
          <>
          <PaginationControls
            page={paginatedItens.safePage}
            pageSize={pageSize}
            totalItems={itens.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              {['Código', 'Item', 'Obra', 'Categoria', 'Qtd', 'V.Unit', 'Total', 'Comprometido', 'Pago', 'Saldo', ''].map(h => <th key={h} style={{ color: '#62666d', fontSize: 11, padding: '10px 12px', textAlign: 'left', fontWeight: 500 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {paginatedItens.items.map(i => {
                const ob = findByRef(obras, i.obra_id)
                const cat = findByRef(categoriasOrcamentarias, i.categoria_orcamentaria_id)
                const ultimaSolicitacao = getUltimaSolicitacaoAlteracao(i.id)
                const ultimaSolicitacaoExclusao = getUltimaSolicitacaoExclusao(i.id)
                const bloqueado = !isPerfilDiretivo(currentUser?.perfil) && isItemBloqueado(i)
                const pendente = ultimaSolicitacao?.status === 'PENDENTE'
                const rejeitada = ultimaSolicitacao?.status === 'REJEITADO'
                const exclusaoPendente = ultimaSolicitacaoExclusao?.status === 'PENDENTE'
                const exclusaoRejeitada = ultimaSolicitacaoExclusao?.status === 'REJEITADO'
                return (
                  <tr key={i.id} className="table-row-hover" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '8px 12px', color: '#8a8f98', fontSize: 11 }}>{i.codigo_item}</td>
                    <td style={{ padding: '8px 12px', color: '#d0d6e0', fontSize: 13 }}>
                      <div>{i.nome_item}</div>
                      {i.item_teto_orcamentario && (
                        <div style={{ color: '#60a5fa', fontSize: 11, marginTop: 3 }}>
                          Teto: {i.item_teto_orcamentario}
                        </div>
                      )}
                      {rejeitada && (
                        <div style={{ color: '#f87171', fontSize: 11, marginTop: 3 }}>
                          Alteracao recusada: {ultimaSolicitacao?.observacao || 'Sem justificativa informada.'}
                        </div>
                      )}
                      {pendente && (
                        <div style={{ color: '#fbbf24', fontSize: 11, marginTop: 3 }}>
                          Alteracao aguardando aprovacao
                        </div>
                      )}
                      {exclusaoPendente && (
                        <div style={{ color: '#fbbf24', fontSize: 11, marginTop: 3 }}>
                          Alteracao aguardando aprovacao
                        </div>
                      )}
                      {exclusaoRejeitada && (
                        <div style={{ color: '#f87171', fontSize: 11, marginTop: 3 }}>
                          Exclusao recusada: {ultimaSolicitacaoExclusao?.observacao || 'Sem justificativa informada.'}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '8px 12px', color: '#8a8f98', fontSize: 12 }}>{ob?.nome_obra || '-'}</td>
                    <td style={{ padding: '8px 12px', color: '#8a8f98', fontSize: 12 }}>{cat?.nome || '-'}</td>
                    <td style={{ padding: '8px 12px', color: '#d0d6e0', fontSize: 13 }}>{i.quantidade_prevista} {i.unidade_medida}</td>
                    <td style={{ padding: '8px 12px', color: '#8a8f98', fontSize: 12 }}>{formatCurrency(i.valor_unitario_previsto)}</td>
                    <td style={{ padding: '8px 12px', color: '#d0d6e0', fontSize: 13, fontWeight: 500 }}>{formatCurrency(i.valor_total_previsto)}</td>
                    <td style={{ padding: '8px 12px', color: '#fbbf24', fontSize: 13 }}>{formatCurrency(i.valor_comprometido)}</td>
                    <td style={{ padding: '8px 12px', color: '#34d399', fontSize: 13 }}>{formatCurrency(i.valor_pago)}</td>
                    <td style={{ padding: '8px 12px', color: i.saldo_atual < 0 ? '#f87171' : '#60a5fa', fontSize: 13, fontWeight: 500 }}>{formatCurrency(i.saldo_atual)}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn-ghost" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => { setForm({ ...i as unknown as Record<string, string> }); setEditId(i.id); setErro(''); setJustificativaAlteracao(''); setShowNovaUnidade(false); setNovaUnidade(''); setModal(true) }}>{bloqueado ? 'Solicitar alteração' : 'Editar'}</button>
                        <button className="btn-ghost" style={{ padding: '3px 10px', fontSize: 12, color: '#f87171' }} onClick={() => deleteItem(i)}>{isPerfilDiretivo(currentUser?.perfil) ? 'Excluir' : 'Solicitar exclusão'}</button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        <PaginationControls
          page={paginatedItens.safePage}
          pageSize={pageSize}
          totalItems={itens.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
        </>
          )}
      </div>

      {modal && (
        <Modal title={editId ? 'Editar Item Orçado' : 'Novo Item Orçado'} onClose={() => setModal(false)}>
          {sel('Obra *', 'obra_id', obras.map(o => ({ value: o.id, label: o.nome_obra })))}
          {sel('Centro de Custo', 'centro_custo_id', centrosFiltrados.map(c => ({ value: c.id, label: c.nome })))}
          {sel('Categoria *', 'categoria_orcamentaria_id', categoriasOrcamentarias.map(c => ({ value: c.id, label: c.nome })))}
          {inp('Nome do Item *', 'nome_item')}
          {inp('Item do Teto Orcamentario', 'item_teto_orcamentario')}
          <p style={{ color: '#8a8f98', fontSize: 11, margin: '-6px 0 12px' }}>
            Informe o codigo ou descricao do item correspondente no relatorio de Teto Orcamentario para manter a rastreabilidade da origem do orcamento.
          </p>
          {inp('Descrição', 'descricao')}
          <div style={{ marginBottom: 12 }}>
            <label className="label-field">Unidade de Medida</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select className="input-field" value={form.unidade_medida || ''} onChange={e => field('unidade_medida', e.target.value)}>
                <option value="">Selecione...</option>
                {unidadesMedidaOptions.map(unidade => <option key={unidade} value={unidade}>{unidade}</option>)}
              </select>
              <button type="button" className="btn-ghost" style={{ whiteSpace: 'nowrap' }} onClick={() => setShowNovaUnidade(prev => !prev)}>
                Cadastrar mais
              </button>
            </div>
            {showNovaUnidade && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <input
                  className="input-field"
                  value={novaUnidade}
                  onChange={e => setNovaUnidade(e.target.value)}
                  placeholder="Ex: m2, m3, kg, vb"
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addUnidadeMedida()
                    }
                  }}
                />
                <button type="button" className="btn-primary" style={{ whiteSpace: 'nowrap' }} onClick={addUnidadeMedida}>
                  Adicionar
                </button>
              </div>
            )}
          </div>
          {inp('Quantidade Prevista', 'quantidade_prevista', 'number')}
          {inp('Valor Unitário Previsto (R$)', 'valor_unitario_previsto', 'number')}
          <div style={{ background: 'rgba(240,64,59,0.08)', border: '1px solid rgba(240,64,59,0.2)', borderRadius: 6, padding: '10px 12px', marginBottom: 12 }}>
            <span style={{ color: '#8a8f98', fontSize: 12 }}>Total calculado: </span>
            <span style={{ color: '#ffb4b1', fontSize: 14, fontWeight: 600 }}>{formatCurrency(total)}</span>
          </div>
          {inp('Etapa de Referência', 'etapa_referencia')}
          {erro && <div style={{ color: '#f87171', fontSize: 12, marginBottom: 12 }}>âš  {erro}</div>}
          <p style={{ color: '#8a8f98', fontSize: 11, margin: '-6px 0 12px' }}>
            Use a etapa de referencia para indicar o marco ou pacote do orcamento aprovado. Quando preenchida, alteracoes feitas por usuarios nao administradores passam por solicitacao de aprovacao.
          </p>
          {(itemBloqueado || itemExigeJustificativaAdmin) && (
            <div style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.24)', borderRadius: 6, padding: '10px 12px', marginBottom: 12 }}>
              <p style={{ color: '#fbbf24', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                {itemExigeJustificativaAdmin ? 'Justificativa obrigatoria' : 'Orcamento bloqueado para edicao direta'}
              </p>
              <p style={{ color: '#8a8f98', fontSize: 12, marginBottom: 8 }}>
                {itemExigeJustificativaAdmin
                  ? 'Administrador e Diretoria podem alterar este item diretamente, mas precisam registrar a justificativa da mudanca.'
                  : 'Este item ja possui etapa definida. Para alterar, informe a justificativa; a solicitacao sera enviada para aprovacao.'}
              </p>
              {itemBloqueado && solicitacaoPendente ? (
                <p style={{ color: '#fbbf24', fontSize: 12 }}>Ja existe uma solicitacao pendente para este item.</p>
              ) : (
                <textarea
                  className="input-field"
                  rows={3}
                  placeholder="Justificativa da alteracao..."
                  value={justificativaAlteracao}
                  onChange={e => setJustificativaAlteracao(e.target.value)}
                />
              )}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn-ghost" onClick={() => { setModal(false); setJustificativaAlteracao('') }}>Cancelar</button>
            <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Salvando...' : itemBloqueado ? 'Solicitar aprovacao' : 'Salvar'}</button>
          </div>
        </Modal>
      )}
    </div>
  )
}

